import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import CookieBanner from "@/components/CookieBanner";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import Login from "./pages/Login";

// Lazy-loaded pages
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Areas = lazy(() => import("./pages/cadastros/Areas"));
const Cargos = lazy(() => import("./pages/cadastros/Cargos"));
const Competencias = lazy(() => import("./pages/cadastros/Competencias"));
const Funcionarios = lazy(() => import("./pages/cadastros/Funcionarios"));
const Avaliadores = lazy(() => import("./pages/cadastros/Avaliadores"));
const PesosCargo = lazy(() => import("./pages/cadastros/PesosCargo"));
const CriteriosDecisao = lazy(() => import("./pages/cadastros/CriteriosDecisao"));
const Ciclos = lazy(() => import("./pages/Ciclos"));
const Avaliacoes = lazy(() => import("./pages/Avaliacoes"));
const AvaliacoesPainel = lazy(() => import("./pages/AvaliacoesPainel"));
const PlanoDesenvolvimento = lazy(() => import("./pages/PlanoDesenvolvimento"));
const Relatorios = lazy(() => import("./pages/Relatorios"));
const AdminUsuarios = lazy(() => import("./pages/admin/Usuarios"));
const AdminLogs = lazy(() => import("./pages/admin/Logs"));
const AdminPerformance = lazy(() => import("./pages/admin/Performance"));
const AdminPrompts = lazy(() => import("./pages/admin/Prompts"));
const ConfiguracoesEmpresa = lazy(() => import("./pages/ConfiguracoesEmpresa"));
const SuperAdminDashboard = lazy(() => import("./pages/superadmin/Dashboard"));
const SuperAdminEmpresas = lazy(() => import("./pages/superadmin/Empresas"));
const SuperAdminEmpresaDetalhe = lazy(() => import("./pages/superadmin/EmpresaDetalhe"));
const SuperAdminAdmins = lazy(() => import("./pages/superadmin/Admins"));
const SuperAdminSuperAdmins = lazy(() => import("./pages/superadmin/SuperAdmins"));
const SuperAdminLogs = lazy(() => import("./pages/superadmin/Logs"));
const SuperAdminPerformance = lazy(() => import("./pages/superadmin/Performance"));
const Privacidade = lazy(() => import("./pages/Privacidade"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <CookieConsentProvider>
          <main>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              {/* SuperAdmin routes */}
              <Route path="/superadmin/dashboard" element={
                <ProtectedRoute allowedRoles={['superadmin']}>
                  <AppLayout><SuperAdminDashboard /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/superadmin/empresas" element={
                <ProtectedRoute allowedRoles={['superadmin']}>
                  <AppLayout><SuperAdminEmpresas /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/superadmin/empresas/:id" element={
                <ProtectedRoute allowedRoles={['superadmin']}>
                  <AppLayout><SuperAdminEmpresaDetalhe /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/superadmin/admins" element={
                <ProtectedRoute allowedRoles={['superadmin']}>
                  <AppLayout><SuperAdminAdmins /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/superadmin/logs" element={
                <ProtectedRoute allowedRoles={['superadmin']}>
                  <AppLayout><SuperAdminLogs /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/superadmin/superadmins" element={
                <ProtectedRoute allowedRoles={['superadmin']}>
                  <AppLayout><SuperAdminSuperAdmins /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/superadmin/performance" element={
                <ProtectedRoute allowedRoles={['superadmin']}>
                  <AppLayout><SuperAdminPerformance /></AppLayout>
                </ProtectedRoute>
              } />

              {/* Rotas protegidas com layout */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <AppLayout><Dashboard /></AppLayout>
                </ProtectedRoute>
              } />

              {/* Cadastros — apenas Admin */}
              <Route path="/cadastros/areas" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AppLayout><Areas /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/cadastros/cargos" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AppLayout><Cargos /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/cadastros/competencias" element={
                <ProtectedRoute allowedRoles={['admin', 'gestor']}>
                  <AppLayout><Competencias /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/cadastros/funcionarios" element={
                <ProtectedRoute allowedRoles={['admin', 'gestor']}>
                  <AppLayout><Funcionarios /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/cadastros/avaliadores" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AppLayout><Avaliadores /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/cadastros/pesos" element={
                <ProtectedRoute allowedRoles={['admin', 'gestor']}>
                  <AppLayout><PesosCargo /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/cadastros/criterios" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AppLayout><CriteriosDecisao /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/ciclos" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AppLayout><Ciclos /></AppLayout>
                </ProtectedRoute>
              } />

              {/* Rotas em desenvolvimento */}
              <Route path="/avaliacoes" element={
                <ProtectedRoute allowedRoles={['admin', 'gestor', 'lider', 'funcionario']}>
                  <AppLayout><Avaliacoes /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/avaliacoes/painel" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AppLayout><AvaliacoesPainel /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/avaliacoes/plano" element={
                <ProtectedRoute allowedRoles={['admin', 'gestor', 'lider']}>
                  <AppLayout><PlanoDesenvolvimento /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/relatorios" element={
                <ProtectedRoute allowedRoles={['admin', 'gestor']}>
                  <AppLayout><Relatorios /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/configuracoes" element={
                <ProtectedRoute allowedRoles={['admin', 'gestor', 'lider', 'funcionario']}>
                  <AppLayout><ConfiguracoesEmpresa /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/usuarios" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AppLayout><AdminUsuarios /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/logs" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AppLayout><AdminLogs /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/performance" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AppLayout><AdminPerformance /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/prompts" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AppLayout><AdminPrompts /></AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/privacidade" element={
                <ProtectedRoute>
                  <AppLayout><Privacidade /></AppLayout>
                </ProtectedRoute>
              } />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </main>
          <CookieBanner />
          </CookieConsentProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
